import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Bytes } from '@/components/Bytes'

import { PageHeader } from '@/components/PageHeader'
import { RevenueChartCard } from '@/components/RevenueChart'
import { UsageChartCard } from '@/components/UsageChart'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  getAdminOverview,
  getRevenueOverview,
  getRevenueSeries,
} from '@/lib/admin/server'
import { formatMicroUsd } from '@/lib/billing/billing'
import { getProductUsage } from '@/lib/usage/server'

export const Route = createFileRoute('/_authenticated/admin/')({
  component: AdminOverviewPage,
})

function AdminOverviewPage() {
  const { data, isPending } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => getAdminOverview(),
  })
  const revenue = useQuery({
    queryKey: ['admin-revenue-overview'],
    queryFn: () => getRevenueOverview(),
  })

  const revLoading = revenue.isPending
  const revenueStats = [
    {
      label: 'Total revenue',
      value:
        revenue.data === undefined
          ? undefined
          : formatMicroUsd(revenue.data.totalRevenueMicros),
    },
    {
      label: 'Revenue (30d)',
      value:
        revenue.data === undefined
          ? undefined
          : formatMicroUsd(revenue.data.last30dRevenueMicros),
    },
    {
      label: 'Transactions',
      value: revenue.data?.purchaseCount,
    },
    {
      label: 'Data sold',
      value:
        revenue.data === undefined ? undefined : (
          <Bytes value={revenue.data.totalBytesSold} unit="gb" />
        ),
    },
  ]

  const stats = [
    {
      label: 'Organizations',
      value: data?.organizationCount,
      to: '/admin/organizations' as const,
    },
    { label: 'Users', value: data?.userCount, to: '/admin/users' as const },
    { label: 'Credentials', value: data?.credentialCount, to: null },
    {
      label: 'Active subscriptions',
      value: data?.subscribedOrgCount,
      to: null,
    },
    {
      label: 'Orgs in overage',
      value: data?.overageOrgCount,
      to: '/admin/organizations' as const,
    },
    {
      label: 'Data balance (all orgs)',
      value:
        data === undefined ? undefined : (
          <Bytes value={data.totalBalanceBytes} unit="gb" />
        ),
      to: null,
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Admin"
        description="Platform-wide view of every organization, user, and proxy credential."
      />
      <RevenueChartCard
        title="Revenue"
        description="Data-purchase revenue across every organization on the platform."
        queryKey={(range) => ['admin-revenue-series', range]}
        fetcher={(range) => getRevenueSeries({ data: { range } })}
        height={300}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {revenueStats.map((s) => (
          <Card key={s.label}>
            <CardHeader>
              <CardDescription>{s.label}</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                {revLoading ? (
                  <Skeleton className="h-7 w-20" />
                ) : (
                  (s.value ?? 0)
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <UsageChartCard
        title="Product usage"
        description="Bandwidth consumed across every organization on the platform."
        queryKey={(range) => ['admin-product-usage', range]}
        fetcher={(range) => getProductUsage({ data: { range } })}
        height={300}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((s) => {
          const body = (
            // biome-ignore lint/correctness/useJsxKeyInIterable: key is set on the Link/div wrapper returned below, not this inner Card
            <Card className={s.to ? 'transition-colors hover:bg-accent' : ''}>
              <CardHeader>
                <CardDescription>{s.label}</CardDescription>
                <CardTitle className="text-3xl">
                  {isPending ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    (s.value ?? 0)
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
          )
          return s.to ? (
            <Link key={s.label} to={s.to}>
              {body}
            </Link>
          ) : (
            <div key={s.label}>{body}</div>
          )
        })}
      </div>
    </div>
  )
}
